"use client";

import { useEffect } from "react";
import { getMeasurementId, trackOutboundLink } from "../../lib/analytics";

function isHttpProtocol(protocol: string): boolean {
  return protocol === "http:" || protocol === "https:";
}

function closestAnchor(target: EventTarget | null): HTMLAnchorElement | null {
  if (!(target instanceof Element)) return null;
  const anchor = target.closest("a");
  return anchor instanceof HTMLAnchorElement ? anchor : null;
}

/**
 * Global delegated click listener that fires a GA4 `click` event (outbound:
 * true) whenever the user navigates to a different origin via an anchor tag.
 *
 * Same-origin, in-app hash, mailto, and tel links are ignored. Middle-click
 * and cmd/ctrl-click are still tracked because the destination navigation
 * still happens (just in a new tab).
 */
export default function OutboundLinkTracker() {
  const measurementId = getMeasurementId();

  useEffect(() => {
    if (!measurementId) return;
    if (typeof window === "undefined") return;

    const onClick = (event: MouseEvent) => {
      const anchor = closestAnchor(event.target);
      if (!anchor) return;
      const href = anchor.href;
      if (!href) return;
      let url: URL;
      try {
        url = new URL(href, window.location.origin);
      } catch {
        return;
      }
      if (!isHttpProtocol(url.protocol)) return;
      if (url.origin === window.location.origin) return;

      trackOutboundLink({
        url: url.href,
        linkText: (anchor.textContent ?? "").trim().slice(0, 120) || undefined,
      });
    };

    const options = { capture: true } as const;
    document.addEventListener("click", onClick, options);
    return () => {
      document.removeEventListener("click", onClick, options);
    };
  }, [measurementId]);

  return null;
}
